/**
 * Bookmarks — user-pinned nav destinations on the bar. Progressive disclosure:
 * 0 pins → nothing, 1 pin → an inline chip, 2+ pins → a "Bookmarks" dropdown.
 * Pins are gated by the SAME two gates as the tree — userCanAccess() for the
 * role and instanceAllows() for the deployment — so a pin the current role
 * can't access, or that this product does not have, is hidden. Pin state lives in useNavPrefsStore; MegaMenu rows toggle it.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import useNavPrefsStore from '../stores/useNavPrefsStore';
import { NAV_TREE } from '../navigation/navTree';
import { userCanAccess, instanceAllows } from '../navigation/menuConfig';
import { Icon } from './navIcons';

// to → {label, icon, require} lookup from the tree (built once).
const ITEM_INDEX: Record<string, { label: string; icon?: string; require?: string; instances?: string[] }> = (() => {
  const m: Record<string, { label: string; icon?: string; require?: string; instances?: string[] }> = {};
  NAV_TREE.forEach((d) =>
    d.columns.forEach((c) =>
      c.items.forEach((i) => {
        m[i.to] = { label: i.label, icon: i.icon || d.icon, require: i.require, instances: i.instances };
      }),
    ),
  );
  return m;
})();

const triggerClass =
  'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[13px] text-on-surface-variant hover:text-white hover:bg-white/[0.04] transition-colors whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60';

export const Bookmarks: React.FC<{ user: any }> = ({ user }) => {
  const history = useHistory();
  const bookmarks = useNavPrefsStore((s) => s.bookmarks);
  const toggleBookmark = useNavPrefsStore((s) => s.toggleBookmark);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const visible = bookmarks
    .map((to) => ({ to, ...ITEM_INDEX[to] }))
    .filter((b) => b.label && instanceAllows(b) && userCanAccess(b, user));

  if (visible.length === 0) return null;

  const go = (to: string) => {
    history.push(to);
    setOpen(false);
  };

  if (visible.length === 1) {
    const b = visible[0];
    return (
      <div className="hidden md:flex items-center">
        <button onClick={() => go(b.to)} aria-label={b.label} className={triggerClass}>
          <Icon name={b.icon || 'star'} className="w-3.5 h-3.5 text-primary" />
          <span className="hidden lg:inline">{b.label}</span>
        </button>
        <div className="w-px h-5 bg-gray-700 mx-1.5" />
      </div>
    );
  }

  return (
    <div ref={ref} className="hidden md:flex items-center relative">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Bookmarks"
        aria-haspopup="menu"
        aria-expanded={open}
        className={triggerClass}
      >
        <Icon name="star" className="w-3.5 h-3.5 text-primary" />
        <span className="hidden lg:inline">Bookmarks</span>
        <span className="text-[10px] text-on-surface-variant tabular-nums">{visible.length}</span>
        <Icon name="chevron" strokeWidth={2.4} className={`w-2.5 h-2.5 opacity-60 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute top-full left-0 mt-2 z-50 min-w-[212px] bg-[#242424] border border-gray-700/60 rounded-xl shadow-2xl shadow-black/50 p-1.5"
        >
          <div className="text-[9.5px] tracking-[0.16em] uppercase text-on-surface-variant font-semibold px-2.5 pt-1.5 pb-1">Bookmarks</div>
          {visible.map((b) => (
            <div key={b.to} className="group flex items-center rounded-lg hover:bg-[#323232]">
              <button
                role="menuitem"
                onClick={() => go(b.to)}
                className="flex-1 flex items-center gap-2.5 px-2.5 py-2 text-[13px] text-on-surface group-hover:text-white text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded-lg"
              >
                <Icon name={b.icon || 'star'} className="w-3.5 h-3.5 text-primary shrink-0" />
                <span className="flex-1">{b.label}</span>
              </button>
              <button
                aria-label={`Remove ${b.label} bookmark`}
                onClick={() => toggleBookmark(b.to)}
                className="px-2 text-on-surface-variant opacity-60 hover:opacity-100 hover:text-red-400 text-lg leading-none focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 rounded"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="w-px h-5 bg-gray-700 mx-1.5" />
    </div>
  );
};

export default Bookmarks;
